import React from 'react'
import { observer } from 'mobx-react'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
} from 'components/Table/ClickableTable'
import JobsListEmpty from 'jobs/components/JobsListEmpty'
import { Id } from 'types/definitions'
import JobsTableRow from './JobsTableRow'
import JobManagementViewModel from '../JobManagementViewModel'
import styles from './JobsTable.sass'
import { JobScopeFilterOption } from 'jobs/infra/JobSearchViewModel'
import Checkbox from '../../../../components/Checkbox'
import { ColumnId } from '../../../infra/JobsTableColumns'
import { match } from '../../../../utils/pattern-match-util'
import { SelectionState } from '../../../../admin/staffing/components/StaffingTable/StaffingSearchViewModel'

/**
 * Maps column names to their respective styles.
 */
export const jobTableColumnStyles: Record<string, any> = {
  'Client name': styles.primaryColumn,
  'Job title': styles.primaryColumn,
  'Job ID': styles.shortIdColumn,
  'Job type': styles.jobTypeColumn,
  Workspace: styles.workspaceColumn,
  Status: styles.statusColumn,
  Milestone: styles.milestonesColumn,
  'Waiting on': styles.waitingOnColumn,
  'Client team': styles.teamColumn,
  'Pro team': styles.teamColumn,
  'Job progress': styles.progressionColumn,
  'Job scope updates': styles.scopeChangeRequestStatusColumn,
  'Client champion': styles.championColumn,
  'Pro champion': styles.championColumn,
  'Is requested pro': styles.isRequestedProColumn,
  Payout: styles.paymentColumn,
  Total: styles.paymentColumn,
  'Total paid': styles.paymentColumn,
  Created: styles.dateColumn,
  Submitted: styles.dateColumn,
  Claimed: styles.dateColumn,
  Deadline: styles.dateColumn,
  Completed: styles.dateColumn,
  'Last activity': styles.dateColumn,
}

interface JobsTableProps {
  vm: JobManagementViewModel
}

export default observer(function JobsTable({ vm }: JobsTableProps) {
  const columns = vm.columnManager.filteredColumns

  return (
    <div>
      {!vm.showEmptyView ? (
        <>
          <Table striped className={styles.jobsTable}>
            <TableHead className={styles.tableHead}>
              {columns
                .filter((col) => col.visible)
                .map((col) =>
                  col.id === ColumnId.Checkbox ? (
                    <TableHeader
                      key={col.id}
                      alignment={'left'}
                      className={styles.checkboxColumn}
                    >
                      <Checkbox
                        onChange={() =>
                          vm.selectionVM.toggleAllSelectedJobsForActions()
                        }
                        checked={match(vm.selectionVM.jobActionSelectionState, {
                          [SelectionState.AllSelected]: () => true,
                          [SelectionState.NoneSelected]: () => false,
                          [SelectionState.SomeSelected]: () =>
                            'indeterminate' as boolean | 'indeterminate',
                        })}
                        loading={vm.selectionVM.fetchAllJobs.pending}
                      />
                    </TableHeader>
                  ) : (
                    <TableHeader
                      className={jobTableColumnStyles[col.header]}
                      key={col.id}
                      canSort={col.sortable}
                      sortOrder={vm.getSortOrder(col)}
                      onSort={(newSort) => {
                        vm.setSortOrder(col, newSort)
                      }}
                    >
                      {col.header}
                    </TableHeader>
                  ),
                )}
            </TableHead>
            <TableBody>
              {vm.filteredJobs.map((job) => (
                <JobsTableRow vm={vm} job={job} key={Id.assert(job.id)} />
              ))}
            </TableBody>
          </Table>
          <InfiniteScrollLoader scrollProvider={vm.searcher.scroller} />
        </>
      ) : (
        <div className={styles.emptyView}>
          <JobsListEmpty
            onClearSearchFilterClick={() => {
              vm.searcher.clearFilters()
            }}
            onSetJobScope={
              vm.searcher.scope === JobScopeFilterOption.Mine
                ? () => vm.searcher.setScope(JobScopeFilterOption.All)
                : undefined
            }
          />
        </div>
      )}
    </div>
  )
})
